import type { ReactNode } from "react";
import { Knopf } from "./knopf";

/**
 * Kurzfassung eines Objekts aus einem anderen Modul (M-008 D5, Design-Guide `detail-formen.md`): Pfad, Titel, Kern-Eigenschaften,
 * höchstens die Hauptaktion und „Im Modul öffnen“. Inline in einer Spalte (Liste | Detail, D4) statt als Modal; derselbe Inhalt.
 * Der Baustein prüft keine Rechte: Titel und Eigenschaften reicht der Aufrufer erst nach der Leseprüfung des Objekts durch,
 * `oeffnen` nur mit dem Recht des Zielmoduls (Anbieten ist nie großzügiger als Erlauben, r110). Die Hauptaktion läuft über die
 * Actions des Fachmoduls (`aktion` ist ein Slot des Aufrufers), nie über einen eigenen Schreibweg.
 */
export interface KurzfassungEigenschaft {
  label: string;
  wert: ReactNode;
}

export function Kurzfassung({
  id,
  pfad,
  titel,
  symbol,
  eigenschaften,
  beschreibung,
  aktion,
  oeffnen,
}: {
  id: string;
  /** Pfad zum Objekt (Modul › Art), von links nach rechts; höchstens drei Glieder. */
  pfad: readonly string[];
  titel: string;
  /** Lucide-Symbol der Objektart (Aufrufer setzt `shrink-0 size-4 aria-hidden`). */
  symbol?: ReactNode;
  eigenschaften: readonly KurzfassungEigenschaft[];
  /** Kurzer Beschreibungstext (nur Klartext, bereits gekürzt). */
  beschreibung?: string | null;
  /** Hauptaktion des Objekts (Slot, Knöpfe des Aufrufers); fehlt sie, steht nur „Im Modul öffnen“. */
  aktion?: ReactNode;
  /** Absprung ins Fachmodul; fehlt er (kein Recht des Ziels), steht kein Link. */
  oeffnen?: { href: string; text: string };
}) {
  const titelId = `${id}-titel`;
  return (
    <>
      {/* Preline: productivity/work-management/projects.html#project-details (Kurzfassung, M-008 D5) */}
      <section id={id} aria-labelledby={titelId} className="bg-card border border-card-line shadow-2xs rounded-xl">
        <div className="p-5 border-b border-card-line">
          <p className="text-xs text-muted-foreground-1">{pfad.join(" › ")}</p>
          <div className="mt-1 flex items-start gap-x-2">
            {symbol && <span className="mt-0.5 flex shrink-0 text-muted-foreground-1">{symbol}</span>}
            <h2 id={titelId} className="min-w-0 text-lg font-semibold text-foreground wrap-break-word">
              {titel}
            </h2>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {beschreibung && <p className="text-sm text-foreground wrap-break-word">{beschreibung}</p>}
          <dl className="grid grid-cols-3 gap-x-3 gap-y-2.5">
            {eigenschaften.map((e) => (
              <div key={e.label} className="col-span-3 grid grid-cols-3 gap-x-3 items-baseline">
                <dt className="col-span-1 text-sm text-muted-foreground-1">{e.label}</dt>
                <dd className="col-span-2 min-w-0 text-sm text-foreground wrap-break-word">{e.wert}</dd>
              </div>
            ))}
          </dl>
          {aktion && <div className="flex flex-wrap items-center gap-2">{aktion}</div>}
        </div>
        {oeffnen && (
          <div className="px-5 py-3 border-t border-card-footer">
            <Knopf href={oeffnen.href} groesse="sm">
              {oeffnen.text}
            </Knopf>
          </div>
        )}
      </section>
    </>
  );
}
